/// <reference types="@types/dom-mediacapture-record" />
import { logger } from '@getaccept/lib-shared/src/logger';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';

export function getVideoError(error: any): string {
  switch (error.name) {
    case 'PermissionDeniedError':
    case 'TypeError':
    case 'NotFoundError':
    case 'NotAllowedError':
      return t("Can't access your web camera");
    default:
      return t(error.name);
  }
}

export function createFileFromBlob(blob: Blob): File {
  const ISOString: string = new Date().toISOString();
  const formattedISOString: string = formatISOString(ISOString);
  const match = blob.type.match(/video\/(\w*)?(;|$)/);
  const ext = match && match[1] ? match[1] : 'webm';
  const fileName = `${formattedISOString}.${ext}`;
  return new File([blob], fileName, { type: blob.type });
}

export function createJpegFileFromBlob(blob: Blob): File {
  const ISOString: string = new Date().toISOString();
  const formattedISOString: string = formatISOString(ISOString);
  const fileName = `${formattedISOString}.jpg`;
  return new File([blob], fileName, {
    lastModified: new Date().getTime(),
    type: 'image/jpeg',
  });
}

export function formatISOString(ISOString: string): string {
  return ISOString.replace(/:|\.|Z/g, '').replace('T', '_');
}

export function getSupportedMimeType(recorder: any = null): string {
  const mimeType = 'video/webm;codecs=vp9,opus';
  const mimeTypeFallback = 'video/webm';
  const mimeTypeFallbackTwo = 'video/mp4';
  const mediaRecorder = recorder || MediaRecorder;
  if (mediaRecorder.isTypeSupported(mimeType)) {
    return mimeType;
  }
  if (mediaRecorder.isTypeSupported(mimeTypeFallback)) {
    return mimeTypeFallback;
  }
  if (mediaRecorder.isTypeSupported(mimeTypeFallbackTwo)) {
    return mimeTypeFallbackTwo;
  }
  return '';
}

export function isBrowserCompatible(userAgent: string): boolean {
  return (
    userAgent.includes('MSIE') ||
    userAgent.includes('Safari') ||
    userAgent.includes('Chrome') ||
    userAgent.includes('Mozilla')
  );
}

export async function calculateMediaDuration(file: File | Blob): Promise<number> {
  const context = new AudioContext();

  try {
    const buffer = await file.arrayBuffer();
    const audio = await context.decodeAudioData(buffer);
    return audio.duration;
  } catch (error) {
    logger.error('Error calculating media duration:', error);
    return 0;
  } finally {
    context.close();
  }
}

function setVideoDurationInSessionStorage(videoId: string, duration: number): void {
  sessionStorage.setItem(`video-${videoId}`, duration.toString());
}

export async function calculateAndSetVideoDurationInSessionStorage(
  videoId: string,
  file: File | Blob
): Promise<void> {
  const duration = await calculateMediaDuration(file);
  setVideoDurationInSessionStorage(videoId, duration ?? 0);
}

export function getVideoDurationFromSessionStorage(videoId: string): number {
  return Number(sessionStorage.getItem(`video-${videoId}`));
}

export async function dataURLtoFile(dataURL: string, filename: string): Promise<File | null> {
  try {
    const arr = dataURL.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch && mimeMatch[1] ? mimeMatch[1] : 'image/jpeg';

    if (!arr[1]) return null;

    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    const ext = mime.split('/')[1] || 'jpg';
    const fullFilename = filename.endsWith(`.${ext}`) ? filename : `${filename}.${ext}`;

    return new File([u8arr], fullFilename, { type: mime });
  } catch (error) {
    return null;
  }
}
